import React, { useReducer } from "react";

declare const umami: { track: (event: string, data?: Record<string, unknown>) => void } | undefined;
import { createRoot } from "react-dom/client";
import { Route, Switch, useLocation, Link } from "wouter";
import "./styles.css";
import type {
  SearchResults,
  NormalizedProduct,
  ShoppingListItem,
  BulkSearchResponse,
  SourceId,
} from "../types";
import { SearchBar } from "./components/SearchBar";
import { ProductPicker } from "./components/ProductPicker";
import { MatchConfirm } from "./components/MatchConfirm";
import { ShoppingList } from "./components/ShoppingList";
import { Comparison } from "./components/Comparison";
import { BulkInput } from "./components/BulkInput";
import { ThemeToggle } from "./components/ThemeToggle";

interface AppState {
  searchTerm: string;
  searchResults: SearchResults | null;
  loading: boolean;
  bulkLoading: boolean;
  bulkSkipped: string[];
  shoppingList: ShoppingListItem[];
  currentItem: Partial<ShoppingListItem> | null;
}

type Action =
  | { type: "SET_LOADING"; loading: boolean }
  | { type: "SET_RESULTS"; term: string; results: SearchResults }
  | { type: "PICK_PRIMARY"; product: NormalizedProduct }
  | {
      type: "CONFIRM_MATCHES";
      matches: Partial<Record<SourceId, NormalizedProduct | null>>;
    }
  | { type: "ADD_TO_LIST" }
  | { type: "REMOVE_FROM_LIST"; id: string }
  | { type: "RESET_SEARCH" }
  | { type: "BULK_LOADING" }
  | { type: "BULK_COMPLETE"; items: ShoppingListItem[]; skipped: string[] }
  | { type: "DISMISS_SKIPPED" };

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.loading };
    case "SET_RESULTS":
      return {
        ...state,
        searchTerm: action.term,
        searchResults: action.results,
        loading: false,
      };
    case "PICK_PRIMARY":
      return {
        ...state,
        currentItem: {
          searchTerm: state.searchTerm,
          primary: action.product,
          matches: {},
        },
      };
    case "CONFIRM_MATCHES":
      return {
        ...state,
        currentItem: state.currentItem
          ? { ...state.currentItem, matches: action.matches }
          : null,
      };
    case "ADD_TO_LIST": {
      if (!state.currentItem?.primary) return state;
      const item: ShoppingListItem = {
        id: crypto.randomUUID(),
        searchTerm: state.currentItem.searchTerm ?? "",
        primary: state.currentItem.primary,
        matches: state.currentItem.matches ?? {},
      };
      return {
        ...state,
        shoppingList: [...state.shoppingList, item],
        currentItem: null,
        searchTerm: "",
        searchResults: null,
      };
    }
    case "REMOVE_FROM_LIST":
      return {
        ...state,
        shoppingList: state.shoppingList.filter((i) => i.id !== action.id),
      };
    case "RESET_SEARCH":
      return {
        ...state,
        searchTerm: "",
        searchResults: null,
        currentItem: null,
      };
    case "BULK_LOADING":
      return { ...state, bulkLoading: true };
    case "BULK_COMPLETE":
      return {
        ...state,
        bulkLoading: false,
        shoppingList: [...state.shoppingList, ...action.items],
        bulkSkipped: action.skipped,
      };
    case "DISMISS_SKIPPED":
      return { ...state, bulkSkipped: [] };
    default:
      return state;
  }
}

const initialState: AppState = {
  searchTerm: "",
  searchResults: null,
  loading: false,
  bulkLoading: false,
  bulkSkipped: [],
  shoppingList: [],
  currentItem: null,
};

function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [, navigate] = useLocation();

  async function handleSearch(term: string) {
    dispatch({ type: "SET_LOADING", loading: true });
    try {
      const res = await fetch(
        `/api/search?q=${encodeURIComponent(term)}`
      );
      const data: SearchResults = await res.json();
      dispatch({ type: "SET_RESULTS", term, results: data });
      const sources = Object.keys(data).filter(k => data[k as SourceId]?.length);
      umami?.track("search", { term, results: sources.length, sources: sources.join(",") });
      navigate("/results");
    } catch {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  }

  async function handlePickPrimary(product: NormalizedProduct) {
    dispatch({ type: "PICK_PRIMARY", product });
    umami?.track("product_selected", { name: product.name, source: product.source, price: product.price });
    navigate("/confirm");
    try {
      const res = await fetch("/api/match", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          product,
          term: state.searchTerm,
        }),
      });
      const data = await res.json();
      const matches: Partial<Record<SourceId, NormalizedProduct | null>> = {};
      for (const [source, match] of Object.entries(data.matches)) {
        matches[source as SourceId] = (match as any)?.product ?? null;
      }
      matches[product.source] = product;
      dispatch({ type: "CONFIRM_MATCHES", matches });
    } catch {
      // Matches will be empty, user can still add
    }
  }

  function handleConfirmAndAdd(
    matches: Partial<Record<SourceId, NormalizedProduct | null>>
  ) {
    dispatch({ type: "CONFIRM_MATCHES", matches });
    dispatch({ type: "ADD_TO_LIST" });
    const matchedSources = Object.entries(matches).filter(([, v]) => v).map(([k]) => k);
    umami?.track("item_added", { term: state.searchTerm, sources_matched: matchedSources.length });
    navigate("/list");
  }

  function handleResetSearch() {
    dispatch({ type: "RESET_SEARCH" });
    navigate("/");
  }

  async function handleBulkSearch(text: string) {
    dispatch({ type: "BULK_LOADING" });
    try {
      const res = await fetch("/api/bulk-search", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const data: BulkSearchResponse = await res.json();
      dispatch({ type: "BULK_COMPLETE", items: data.items, skipped: data.skipped });
      umami?.track("bulk_search", { items_found: data.items.length, items_skipped: data.skipped.length });
      navigate("/list");
    } catch {
      dispatch({ type: "BULK_COMPLETE", items: [], skipped: [] });
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <Link href="/" className="no-underline">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Compara Preços
            </h1>
          </Link>
          <ThemeToggle />
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Compare precos entre supermercados de BH
        </p>
      </header>

      <Switch>
        <Route path="/">
          <SearchBar
            onSearch={handleSearch}
            loading={state.loading}
            disabled={state.loading || state.bulkLoading}
          />
          <BulkInput
            onSubmit={handleBulkSearch}
            loading={state.bulkLoading}
          />
        </Route>

        <Route path="/results">
          {state.searchResults ? (
            <>
              <SearchBar
                onSearch={handleSearch}
                loading={state.loading}
                disabled={state.loading}
              />
              <ProductPicker
                results={state.searchResults}
                onPick={handlePickPrimary}
                onBack={handleResetSearch}
              />
            </>
          ) : (
            <MetaRedirect to="/" navigate={navigate} />
          )}
        </Route>

        <Route path="/confirm">
          {state.currentItem && state.searchResults ? (
            <>
              <SearchBar
                onSearch={handleSearch}
                loading={state.loading}
                disabled={state.loading}
              />
              <MatchConfirm
                item={state.currentItem}
                results={state.searchResults}
                onConfirm={handleConfirmAndAdd}
                onBack={() => navigate("/results")}
              />
            </>
          ) : (
            <MetaRedirect to="/" navigate={navigate} />
          )}
        </Route>

        <Route path="/list">
          <SearchBar
            onSearch={handleSearch}
            loading={state.loading}
            disabled={state.loading}
          />
          {state.bulkSkipped.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg flex items-start justify-between">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Nao encontrado: {state.bulkSkipped.join(", ")}
              </p>
              <button
                onClick={() => dispatch({ type: "DISMISS_SKIPPED" })}
                className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-200 ml-2 shrink-0"
              >
                x
              </button>
            </div>
          )}
          <ShoppingList
            items={state.shoppingList}
            onRemove={(id) => dispatch({ type: "REMOVE_FROM_LIST", id })}
            onAddMore={handleResetSearch}
            onCompare={() => { umami?.track("comparison_opened", { items: state.shoppingList.length }); navigate("/compare"); }}
          />
        </Route>

        <Route path="/compare">
          <Comparison
            items={state.shoppingList}
            onBack={() => navigate("/list")}
          />
        </Route>

        <Route>
          <MetaRedirect to="/" navigate={navigate} />
        </Route>
      </Switch>

      {state.shoppingList.length > 0 && (
        <Route path="/:any*">
          {({ any }) =>
            any !== "list" && any !== "compare" ? (
              <Link
                href="/list"
                className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-sm no-underline"
              >
                Lista ({state.shoppingList.length})
              </Link>
            ) : null
          }
        </Route>
      )}
    </div>
  );
}

function MetaRedirect({ to, navigate }: { to: string; navigate: (path: string) => void }) {
  React.useEffect(() => { navigate(to); }, []);
  return null;
}

const root = createRoot(document.getElementById("root")!);
root.render(<App />);
